

import React, { useContext, useState, useRef, useEffect } from 'react'
import { GlobalContext } from '../GlobalContext'
import CloudformationOutputs from '../CloudformationOutputs.json'
import RedAlert from '../ui-elements/RedAlert'
import Button from '../formfields/Button'
import { data } from '@tensorflow/tfjs'
import { useLocation } from 'react-router-dom'
import { useActivityBroadcast } from '../context/activity/ActivityBroadcastContext'

const canvasWidthProportion = 0.8
const canvasWidthMaximum = 500

type Props = {
    setSignatureCanvasData: React.Dispatch<React.SetStateAction<any>>
}

function SignatureCanvas({
    setSignatureCanvasData
}: Props) {

    const { t } = useContext(GlobalContext)
    const { broadcastThrottled } = useActivityBroadcast()

    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const [hasDrawingStarted, setHasDrawingStarted] = useState(false)
    const [isDrawing, setIsDrawing] = useState(false)
    const [isViewportTallEnough, setIsViewportTallEnough] = useState(true)
    const [lastPos, setLastPos] = useState({ x: 0, y: 0 })
    const location = useLocation()

    // need to prevent default on the following events on the canvas to stop mobile browsers from misinterpreting 
    // gestures
    //
    // gesturestart, contextmenu 

    
    useEffect(() => {
        if (canvasRef.current) {
            canvasRef.current.addEventListener('gesturestart', (e: any) => {
                e.preventDefault()
            })
            canvasRef.current.addEventListener('contextmenu', (e: any) => {
                e.preventDefault()
            })
            // console.log("ignoring gesturestart and contextmenu events")
        }
    }, [canvasRef.current])

    useEffect(() => {
        handleScrolling()
        const canvas: any = canvasRef.current

        if (canvas) {
            const ctx = canvas.getContext('2d')
            canvas.width = (window.innerWidth * canvasWidthProportion) < canvasWidthMaximum ? (window.innerWidth * canvasWidthProportion) : canvasWidthMaximum
            canvas.height = canvas.width * 0.5
            canvas.background = '#ffffff'
            ctx.lineWidth = 2
            ctx.lineCap = 'round'
            ctx.strokeStyle = 'black'
            ctx.fillStyle = 'white'
            ctx.fillRect(0, 0, canvas.width, canvas.height)

            const handleResize = () => {
                handleScrolling()

                const newWidth = (window.innerWidth * canvasWidthProportion) < canvasWidthMaximum ? (window.innerWidth * canvasWidthProportion) : canvasWidthMaximum
                const newHeight = newWidth * 0.5
                const currentWidth = canvas.width
                const diff = Math.abs(currentWidth - newWidth)
                if (diff > (newWidth / 10)) {
                    setSignatureCanvasData('')
                    canvas.width = newWidth
                    canvas.height = newHeight
                    ctx.fillStyle = 'white'
                    ctx.fillRect(0, 0, canvas.width, canvas.height)
                }

            }

            window.addEventListener('resize', handleResize);
            // window.addEventListener('orientationchange', stopScrolling);
            return () => {
                clearBodyStyles()
                window.removeEventListener('resize', handleResize);
                // window.removeEventListener('orientationchange', stopScrolling);
            }
        } else {
            console.log('no canvas')
        }
    }, [])

    useEffect(() => {
        // clear the canvas if the route changes
        clearCanvas()
      }, [location])


    const handleScrolling = () => {
        // Mobile: the browser scrolling makes it difficult to interact with the canvas.
        // Lock the window orientation and disable scrolling on these devices.
        if (window.innerWidth < window.innerHeight && (window.innerHeight < 600)) {
            setIsViewportTallEnough(true)
        } else {
            if (window.innerHeight > 600) {
                setIsViewportTallEnough(true)
            } else {
                console.log('viewport not tall enough')

                setIsViewportTallEnough(false)
            }
        }
        if (
            window.innerWidth < window.innerHeight &&
            navigator.userAgent.includes('iPhone')
        ) {
            document.body.className = 'portraitMode'
        } else {
            document.body.className = ''
        }
    }

    const clearBodyStyles = () => {
        document.body.className = ''
    }


    const getPos = (e: any) => {
        // @ts-ignore
        const rect = canvasRef.current.getBoundingClientRect()
        if (e.touches && e.touches.length > 0) {
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top,
            }
        } else {
            return {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            }
        }
    }

    const startDrawing = (e: any) => {
        //document.body.style.overflow = 'hidden';
        const pos = getPos(e)
        setLastPos(pos)
        setIsDrawing(true)
        setHasDrawingStarted(true)
    }

    const draw = (e: any) => {
        if (!isDrawing) return
        broadcastThrottled('isDrawingSignature')
        // @ts-ignore
        const ctx = canvasRef.current.getContext('2d')
        const pos = getPos(e)
        // set line with to be relatively fat so it looks ok when scaled down 
        ctx!.lineWidth = 6
        ctx!.lineCap = 'round'
        ctx!.beginPath()
        ctx!.moveTo(lastPos.x, lastPos.y)
        ctx!.lineTo(pos.x, pos.y)
        ctx!.stroke()

        setLastPos(pos)
    }

    const stopDrawing = () => {
        // only stop drawing if some drawing has already started
        if (isDrawing) {
            setIsDrawing(false)
            const canvas: any = canvasRef.current
            const dataUrl = canvas && canvas.toDataURL('image/jpeg') || ''
            setSignatureCanvasData(dataUrl)
        }
    }

    const clearCanvas = () => {
        const canvas: any = canvasRef.current
        const ctx = canvas.getContext('2d')
        canvas.width = canvas.width // Reset the canvas
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        setHasDrawingStarted(false)
        setSignatureCanvasData('')
    }




    return <div className={`w-full flex flex-col items-center justify-center gap-4`}>
        {!isViewportTallEnough && <RedAlert>{t('Dashboard.SignatureCanvas.portraitMode')}</RedAlert>}
        <div className={`${isViewportTallEnough ? 'flex flex-col gap-4 ' : ' hidden '} `}>
            <p className={`text-center`}>{t('Dashboard.SignatureCanvas.drawSignature')}</p>
            <div className='relative'>
                <canvas
                    style={{ touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' } as React.CSSProperties}
                    ref={canvasRef}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                />
                {hasDrawingStarted && <div onClick={clearCanvas} className={`absolute top-0 right-0 text-md hover:opacity-80 rounded bg-gray-300 flex items-center justify-center cursor-pointer m-1`}>
                    <button
                        className={`outline-none border-none cursor-pointer w-6 h-6 font-white flex items-center justify-center`}
                    >✕</button>
                    <p className={`py-1 pr-2`}>{t('Dashboard.SignatureCanvas.clear')}</p>
                </div>}
            </div>
        </div>
    </div>
}


export default SignatureCanvas


